import { SxProps, Theme } from "@mui/material";
import settingsShellTokens, {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";
import apiKeysTokens from "./ApiKeys.tokens";

/**
 * Phone and tablet rules for **Settings → Integrations**.
 *
 * This screen has no mock in `Settings - Mobile & Tablet.dc.html`, and unlike
 * the other settings screens it never had a `*.tokens.ts` — its desktop look is
 * built straight from the MUI theme. So this file holds only what the small
 * viewports need, in the language the neighbouring screens already use: a
 * full-width 44px primary action and one card per record in place of the
 * five-column table. Everything here is scoped below `md`; desktop is
 * untouched.
 */
const shellPhone = settingsShellResponsiveTokens.phone;

const { card, pageSubtitle, primaryButton, sectionHeader, list } = apiKeysTokens;

export const apiKeys = {
  /**
   * The screen supplies its own title node (title + strapline), so it has to
   * reproduce what `SettingsLayout` gives a plain string title — including the
   * step down to the tablet's 20px.
   */
  pageTitleSx: {
    color: settingsShellTokens.colors.pageTitle,
    fontSize: {
      sm: settingsShellResponsiveTokens.tablet.pageTitleFontSize,
      md: settingsShellTokens.pageTitle.fontSize,
    },
    fontWeight: settingsShellTokens.pageTitle.fontWeight,
    letterSpacing: settingsShellTokens.pageTitle.letterSpacing,
    lineHeight: settingsShellTokens.pageTitle.lineHeight,
  } as SxProps<Theme>,

  pageSubtitleSx: {
    mt: pageSubtitle.marginTop,
    color: pageSubtitle.color,
    fontSize: pageSubtitle.fontSize,
    fontWeight: pageSubtitle.fontWeight,
    lineHeight: pageSubtitle.lineHeight,
  } as SxProps<Theme>,

  /** White rounded card, same chrome as every other Settings card. */
  cardSx: {
    bgcolor: "background.paper",
    border: `${card.borderWidth} solid ${card.border}`,
    borderRadius: card.radius,
    boxShadow: card.shadow,
    boxSizing: "border-box",
    overflow: "hidden",
  } as SxProps<Theme>,

  /** Card header: glyph + copy. The action lives in the page header now. */
  sectionHeaderSx: {
    px: { xs: "14px", md: "18px" },
    py: { xs: "9px", md: "10px" },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: { xs: "10px", md: "12px" },
    flexWrap: "wrap",
  } as SxProps<Theme>,

  sectionGlyphSx: {
    width: sectionHeader.glyphSize,
    height: sectionHeader.glyphSize,
    borderRadius: sectionHeader.glyphRadius,
    bgcolor: "primary.lightest",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  sectionTitleSx: {
    fontSize: sectionHeader.titleFontSize,
    fontWeight: sectionHeader.titleFontWeight,
    color: sectionHeader.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  sectionDescSx: {
    display: "block",
    mt: "2px",
    fontSize: sectionHeader.descFontSize,
    color: sectionHeader.descColor,
    lineHeight: 1.45,
  } as SxProps<Theme>,

  /** Primary action, sized like the one on Roles, Teams and Enterprise SSO. */
  createButtonSx: {
    // Full width at 44px below `md`, as on every other settings screen.
    width: { xs: "100%", md: "auto" },
    height: { xs: "44px", md: `${primaryButton.height}px` },
    minWidth: 0,
    px: { xs: "20px", md: primaryButton.paddingX },
    py: 0,
    borderRadius: primaryButton.radius,
    fontSize: primaryButton.fontSize,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    flexShrink: 0,
    "&:hover": { boxShadow: "none" },
  } as SxProps<Theme>,

  /**
   * The in-place gate banner, inset to the card body's own gutter on all four
   * sides — without the bottom the banner butted straight up against the key
   * list below it.
   */
  gateBannerSx: {
    px: { xs: "16px", md: 2.5 },
    pt: { xs: "16px", md: 2.5 },
    pb: { xs: "16px", md: 2.5 },
  } as SxProps<Theme>,

  /**
   * The key list, rebuilt 1:1 off `MyPlan.styles.ts`'s Invoices list — the
   * column-header strip, the row grid and the status pill all reuse that same
   * shape and geometry, just re-keyed to this screen's own five columns and
   * `ApiKeys.tokens.ts`'s `list` block instead of `myPlanTokens`.
   */

  /** Column-header strip. Unconditional — stays up over an empty or gated
   *  list, exactly as the Invoices header does. */
  columnHeaderSx: (columns: string): SxProps<Theme> => ({
    display: { xs: "none", md: "grid" },
    gridTemplateColumns: columns,
    px: list.space.gutter,
    py: list.space.columnHeaderY,
    bgcolor: list.color.headerBg,
    borderTop: `${list.border.hairline} solid ${list.color.headerBorder}`,
    borderBottom: `${list.border.hairline} solid ${list.color.headerBorder}`,
    fontSize: list.font.columnHeader,
    fontWeight: list.weight.strong,
    letterSpacing: list.letterSpacing.columnHeader,
    color: list.color.muted,
    lineHeight: "normal",
  }),

  /** One key's row. Desktop only — the mobile card below takes over under
   *  `md`, same split the Invoices list makes. */
  listRowSx: (columns: string, isLast: boolean): SxProps<Theme> => ({
    display: { xs: "none", md: "grid" },
    gridTemplateColumns: { md: columns },
    alignItems: "center",
    px: list.space.gutter,
    py: list.space.rowY,
    borderBottom: isLast
      ? "none"
      : `${list.border.hairline} solid ${list.color.rowDivider}`,
  }),

  rowTextSx: (color: string, weight: number): SxProps<Theme> => ({
    fontSize: list.font.rowText,
    fontWeight: weight,
    color,
    lineHeight: "normal",
  }),

  /** Second line under the key name — the rate limit. */
  rowMetaLineSx: {
    display: "block",
    mt: "2px",
    fontSize: list.font.rowMeta,
    fontWeight: list.weight.regular,
    color: list.color.muted,
    lineHeight: "normal",
  } as SxProps<Theme>,

  rowActionCellSx: {
    display: "flex",
    justifyContent: "flex-end",
  } as SxProps<Theme>,

  /** Status pill — same stadium shape as the Invoices STATUS pill; only the
   *  fill/text colour changes per state. */
  pillSx: (tone: { bg: string; text: string }): SxProps<Theme> => ({
    display: "inline-flex",
    alignItems: "center",
    fontSize: list.font.chip,
    fontWeight: list.weight.strong,
    color: tone.text,
    bgcolor: tone.bg,
    px: "9px",
    py: "2px",
    borderRadius: list.radius.pill,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  }),

  /** Revoke affordance on a desktop row — a plain icon button, not a card
   *  chip, so it doesn't compete with the status pill beside it. */
  rowRevokeButtonSx: {
    color: list.color.body,
    "&:hover": { color: "error.main" },
  } as SxProps<Theme>,

  /**
   * Below `md` a key is a flat row, not its own bordered card — matching the
   * Invoices list's `invoiceCardRowSx`, which the desktop grid also gives way
   * to at this width.
   */
  keyCardRowSx: (isLast: boolean): SxProps<Theme> => ({
    display: { xs: "flex", md: "none" },
    alignItems: "center",
    gap: "12px",
    px: { xs: "16px", md: 0 },
    py: "14px",
    [PHONE_SM_QUERY]: { paddingLeft: shellPhone.contentGap.sm, paddingRight: shellPhone.contentGap.sm },
    borderBottom: isLast
      ? "none"
      : `${list.border.hairline} solid ${list.color.rowDivider}`,
  }),

  keyCardTextSx: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  } as SxProps<Theme>,

  keyCardTopSx: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  } as SxProps<Theme>,

  keyCardNameSx: {
    fontSize: "13.5px",
    fontWeight: 700,
    color: list.color.ink,
    lineHeight: "normal",
    wordBreak: "break-word",
  } as SxProps<Theme>,

  keyCardMetaSx: {
    fontSize: "12px",
    color: list.color.body,
    lineHeight: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  keyCardActionSx: {
    width: 32,
    height: 32,
    flexShrink: 0,
    color: list.color.body,
    "&:hover": { color: "error.main" },
  } as SxProps<Theme>,
};

export default apiKeys;
